// @flow strict

import { useState, useMemo, useRef, useCallback } from "react";
import { IoIosArrowRoundBack, IoIosArrowRoundForward } from "react-icons/io";
import ServiceCard from "./ServiceCard";
import ServiceListFiltered from "./ServiceListFiltered";
import { motion } from "framer-motion";
import { FaFilter } from "react-icons/fa";
import { FiX } from "react-icons/fi";

function PageSelector({ pages, onSelect }) {
  return (
    <div className="absolute bg-white shadow-md p-2 z-50 max-h-24 overflow-y-scroll overflow-ellipsis ">
      {pages.map((page) => (
        <button
          key={page}
          onClick={() => onSelect(page)}
          className="block px-4 py-2 font-nunito text-[14px] text-text hover:bg-gray-100 w-full text-left"
        >
          {page}
        </button>
      ))}
    </div>
  );
}

function ServicesListPaginated({
  liste = [],
  slice = liste.length,
  search = true,
  filter = true,
  currentPage = 1,
  setCurrentPage = (value) => {
    currentPage = value;
  },
}) {
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [showSelector, setShowSelector] = useState("");
  const [selectorPages, setSelectorPages] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");

  // Déclaration des filtres dans le composant parent
  const [filters, setFilters] = useState({
    category: [],
    minDuration: null,
    maxDuration: null,
    minPrice: null,
    maxPrice: null,
  });

  // Fonction pour gérer les changements de filtres
  const handleFilterChange = useCallback(
    (newFilters) => {
      setFilters(newFilters);
      setCurrentPage(1);
    },
    [setCurrentPage]
  );

  // Fonction pour réinitialiser les filtres
  const handleResetFilters = useCallback(() => {
    setFilters({
      category: [],
      minDuration: null,
      maxDuration: null,
      minPrice: null,
      maxPrice: null,
    });
    setCurrentPage(1);
  }, [setCurrentPage]);

  // Appliquer les filtres sur la liste
  const filteredItems = useMemo(() => {
    return (
      Array.isArray(liste) &&
      liste.filter((item) => {
        const categoryName = item?.category?.name;
        const price = Number(item?.price);
        const duration = Number(item?.durationMinutes);

        return (
          (filters.category.length === 0 ||
            filters.category.includes(categoryName)) &&
          (filters.minPrice === null || price >= filters.minPrice) &&
          (filters.maxPrice === null || price <= filters.maxPrice) &&
          (filters.minDuration === null || duration >= filters.minDuration) &&
          (filters.maxDuration === null || duration <= filters.maxDuration)
        );
      })
    );
  }, [liste, filters]);

  // Appliquer la recherche sur les éléments filtrés
  const filteredServices = useMemo(() => {
    const term = searchTerm?.trim().toLowerCase();

    return (
      Array.isArray(filteredItems) &&
      filteredItems.filter((service) => {
        if (!term) return true;

        const fields = [
          service?.nom,
          service?.description,
          service?.category?.name,
          service?.category?.description,
        ]
          .filter(Boolean)
          .map((f) => f.toLowerCase());

        return fields.some((field) => field.includes(term));
      })
    );
  }, [filteredItems, searchTerm]);

  const itemsPerPage = 6;
  const listeToUse =
    Array.isArray(filteredServices) && filteredServices.slice(0, slice);

  // Calculer le nombre total de pages
  const totalPages = Math.ceil(listeToUse.length / itemsPerPage);

  // Obtenir les éléments pour la page actuelle
  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return (
      Array.isArray(listeToUse) &&
      listeToUse.slice(startIndex, startIndex + itemsPerPage)
    );
  }, [listeToUse, currentPage, itemsPerPage]);

  // Générer les numéros de page à afficher
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisiblePages = 5; // Nombre maximum de pages visibles

    if (totalPages <= maxVisiblePages) {
      // Si le nombre total de pages est inférieur au maximum visible, afficher toutes les pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Toujours afficher la première page
      pages.push(1);

      // Déterminer quelles pages afficher au milieu
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Ajuster si on est près du début
      if (currentPage <= 3) {
        endPage = 4;
      }

      // Ajuster si on est près de la fin
      if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3;
      }

      // Ajouter les points de suspension après la première page si nécessaire
      if (startPage > 2) {
        pages.push("...");
      }

      // Ajouter les pages du milieu
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Ajouter les points de suspension avant la dernière page si nécessaire
      if (endPage < totalPages - 1) {
        pages.push("...");
      }

      // Toujours afficher la dernière page
      pages.push(totalPages);
    }

    return pages;
  }, [currentPage, totalPages]);

  // Aller à la page précédente
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }

    containerRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Aller à la page suivante
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }

    containerRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <div className="relative w-full px-4" ref={containerRef}>
        {search && (
          <div className="sticky top-0 lg:mb-6 justify-end grid lg:grid-cols-2">
            <div className="hidden lg:flex" />
            <div className="relative flex gap-1 items-center focus:outline-none focus:ring-2 focus:ring-secondary">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Rechercher un service..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // reset pagination quand on cherche
                }}
                // autoFocus={true}
                className="flex-1 md:w-1/2 px-4 py-2 font-nunito text-[14px] text-text focus:outline-none focus:ring-0 focus:ring-none"
              />
              {searchTerm.length > 1 && (
                <button
                  className="absolute right-5"
                  aria-label="Effacer la recherche"
                  onClick={() => {
                    setSearchTerm("");
                    setCurrentPage(1);
                    searchInputRef.current?.focus();
                  }}
                >
                  <FiX size={18} />
                </button>
              )}
            </div>
          </div>
        )}

        <div className="grid lg:gap-6 grid-cols-1 lg:grid-cols-4 relative ">
          {filter ? (
            <>
              {/* Filtres desktop */}
              <motion.div
                key="service-filters"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.4 }}
                className="hidden lg:block lg:col-span-1 bg-white z-20 sticky top-20 shadow h-fit"
              >
                <ServiceListFiltered
                  initialList={liste.filter(
                    (service) => service.is_active === true
                  )}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onResetFilters={handleResetFilters}
                />
              </motion.div>

              {/* Bouton pour afficher les filtres sur mobile */}
              <div className="w-full flex items-center justify-end lg:hidden">
                <button
                  className="flex items-center justify-end font-nunito font-semibold text-[14px] text-action px-4 py-2"
                  onClick={() => setShowMobileFilters(true)}
                >
                  <FaFilter className="h-5 w-5 mr-2 text-action" />
                  Filter
                </button>
              </div>
            </>
          ) : null}

          <motion.div
            key="service-list"
            initial={{ opacity: -50, x: 0 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: -50, x: 0 }}
            transition={{ duration: 0.4 }}
            className={`col-span-1 ${
              filter ? "lg:col-span-3" : "lg:col-span-4"
            } `}
          >
            {/* Liste des services */}
            <div
              className={`grid grid-cols-1 ${
                filter
                  ? "md:grid-cols-2  lg:grid-cols-3"
                  : "md:grid-cols-3  lg:grid-cols-4"
              }  gap-6 mb-8`}
            >
              {Array.isArray(currentItems) &&
                currentItems.map((item, index) => (
                  <ServiceCard service={item} key={index} />
                ))}
            </div>
          </motion.div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2 mt-8">
            {/* Flèche gauche */}
            <button
              aria-label={`Page ${pageNumbers}`}
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className={`p-2 ${
                currentPage === 1
                  ? "text-white font-poppins font-bold text-[16px] cursor-not-allowed"
                  : "text-primary hover:bg-secondary-200"
              }`}
            >
              <IoIosArrowRoundBack className="h-5 w-5 text-secondary" />
            </button>

            {pageNumbers.map((pageNumber, index) => {
              if (pageNumber === "...") {
                const previousVisible = pageNumbers[index - 1];
                const nextVisible = pageNumbers[index + 1];

                const range = Array.from(
                  { length: nextVisible - previousVisible - 1 },
                  (_, i) => previousVisible + i + 1
                );

                return (
                  <div key={index} className="relative">
                    <button
                      onClick={() => {
                        setSelectorPages(range);
                        setShowSelector((p) =>
                          p === `selector-${index}` ? "" : `selector-${index}`
                        );
                      }}
                      className="w-10 h-10 flex items-center justify-center font-nunito text-[14px] text-text hover:bg-gray-100"
                    >
                      ...
                    </button>
                    {showSelector === `selector-${index}` && (
                      <PageSelector
                        key={`selector-${index}`}
                        pages={selectorPages}
                        onSelect={(page) => {
                          setCurrentPage(page);
                          setShowSelector(false);
                          containerRef.current?.scrollIntoView({
                            behavior: "smooth",
                          });
                        }}
                      />
                    )}
                  </div>
                );
              }

              return (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentPage(pageNumber);
                    containerRef.current?.scrollIntoView({
                      behavior: "smooth",
                    });
                  }}
                  className={`w-10 h-10 flex items-center justify-center font-nunito text-[14px] ${
                    pageNumber === currentPage
                      ? "bg-secondary text-white"
                      : "text-text hover:bg-secondary-100"
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}

            {/* Flèche droite */}
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className={`p-2 ${
                currentPage === totalPages
                  ? "text-white font-poppins font-bold text-[16px] cursor-not-allowed"
                  : "text-white hover:bg-secondary-200"
              }`}
            >
              <IoIosArrowRoundForward className="h-5 w-5 text-secondary" />
            </button>
          </div>
        )}
      </div>

      {/* Drawner Mobile Filter */}
      <div className="relative my-5 max-w-lg mx-auto">
        {/* Overlay pour mobile quand les filtres sont ouverts */}
        {showMobileFilters && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setShowMobileFilters(false)}
          ></div>
        )}

        {/* Filtres mobiles (drawer) */}
        <div
          className={`
          fixed top-0 left-0 h-full w-50 bg-white z-50 shadow-xl transform transition-transform lg:hidden
          ${showMobileFilters ? "translate-x-0" : "-translate-x-full"}
        `}
        >
          <div className="p-4 h-full overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-poppins font-semibold text-[18px] text-title">
                Filtres
              </h2>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <ServiceListFiltered
              initialList={
                Array.isArray(liste) &&
                liste.filter((service) => service.is_active === true)
              }
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default ServicesListPaginated;